/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {
    IssueFilterInterface,
    IssueInterface,
    IssuesConfigInterface,
    PaginationInterface,
} from '@/store/modules/issues/types';
import {Action, Getter, State} from 'vuex-class';
import {SearchByType, StatusType} from '@/store/modules/search/types';
import {IssueListSelected} from '@/components/IssuesList/types';
import IssueVote from '@/components/IssueVote/index.vue';
import IssueTitleInfo from '@/components/IssueTitleInfo/index.vue';
import {GameInterface, GamePlatformInterface} from '@/store/modules/games/types';
import Loading from '@/components/Loading/index.vue';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import {ReportStageInterface} from '@/store/modules/form/types';
import {CustomerDetailsInterface} from '@/store/modules/customer/types';

const namespace: string = 'issuesStateModule';

/**
 * IssuesList Component
 *
 * @author jgeorgiadou@frontier.co.uk
 */
@Component({
    name: 'c-issues-list',
    components: {
        'c-issue-vote': IssueVote,
        'c-issue-title-info': IssueTitleInfo,
        'c-loading': Loading,
    },
})
export default class IssuesList extends Vue {
    // Issues
    @Action('fetchIssues', {namespace})
    public fetchIssues!: (issuesConfig: IssuesConfigInterface) => Promise<void>;
    @Getter('getTotalIssues', {namespace})
    public getTotalIssues!: (stateName: string) => number;
    @Getter('getIssuesPagination', {namespace})
    public getIssuesPagination!: (stateName: string) => PaginationInterface;
    @Getter('getIssues', {namespace})
    public getIssues!: (stateName: string) => IssueInterface[];
    @Getter('getSearchTerm', {namespace})
    public getSearchTerm!: string;
    @Action('updateSearchTerm', {namespace: 'issuesStateModule'})
    public updateSearchTerm!: (term: string) => void;
    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'})
    public fetchGames!: () => Promise<void>;
    @Action('fetchPlatforms', {namespace: 'gamesStateModule'})
    public fetchPlatforms!: () => Promise<void>;
    @Getter('getCurrentGame', {namespace: 'gamesStateModule'})
    public getCurrentGame!: GameInterface;
    @State('reportStage', {namespace: 'formStateModule'}) public reportStage!: ReportStageInterface;
    @Getter('updateCurrentGameVersion', {namespace: 'gamesStateModule'})
    public updateCurrentGameVersion!: number | null;
    @Action('updateCurrentGame', {namespace: 'gamesStateModule'})
    public updateCurrentGame!: (game: GameInterface | undefined) => void;
    @Getter('getGames', {namespace: 'gamesStateModule'})
    public getGames!: GameInterface[];
    @Getter('getPlatforms', {namespace: 'gamesStateModule'})
    public getPlatforms!: GamePlatformInterface[];
    // Settings masthead
    @Action('updateMastheadImage', {namespace: 'settingsModule'})
    public updateMastheadImage!: (imageSlug: string | undefined) => void;
    // User
    @Action('fetchCustomerDetails', {namespace: 'customerModule'})
    public fetchCustomerDetails!: () => Promise<void>;
    @Getter('getCustomerDetails', {namespace: 'customerModule'})
    public getCustomerDetails!: CustomerDetailsInterface | undefined;
    @Action('removeCustomerDetails', {namespace: 'customerModule'})
    public removeCustomerDetails!: () => void;

    public currentPage: number = 1;
    public searchString: string = '';

    public searchByType: any = SearchByType;
    public statusType: any = StatusType;
    public selected: IssueListSelected = {
        sort: null,
        status: null,
        category: null,
        platform: null,
        version: null,
        game: null,
    };
    public state: LoadingStateInterface = new LoadingState(false, false);
    public debounceInput: Debounce = this.$debounce(this.keywordChanged, 500);
    public selectedStatus: number = 0;
    public versionList: any = [];
    public currentRoute: string = '';
    public numberOfVoteLeft: number = 0;
    public sortByKey: number = 1;
    public versionKey: any = '';
    public categoryKey: any = '';

    /**
     * Issues Getter
     */
    public get issues(): IssueInterface[] {
        return this.getIssues(`${this.stateName}Issues`);
    }

    /**
     * Total Issues getter
     */
    public get totalIssues(): number {
        return this.getTotalIssues(`${this.stateName}Issues`);
    }

    /**
     * Total Pages getter
     */
    public get totalPages(): number {
        return undefined !== this.filter.itemsPerPage ? Math.ceil(this.totalIssues / this.filter.itemsPerPage) : 0;
    }

    /**
     * Pagination getter
     */
    public get pagination(): any {
        return this.getIssuesPagination(`${this.stateName}Issues`);
    }

    @Prop(String) private stateName!: string;
    @Prop(Object) private filter!: IssueFilterInterface;
    @Prop({default: false}) private showPagination!: boolean;
    @Prop({default: false}) private showExposedFilters!: boolean;
    @Prop({default: false}) private showSortBy!: boolean;
    @Prop({default: false}) private showSearchField!: boolean;
    @Prop({default: false}) private showIssueSummary!: boolean;

    // Set the statuses for the filter
    private statuses: {
        id: number,
        name: string,
    }[]  = [
        {
            id: StatusType.UNCONFIRMED,
            name: this.$i18n.translate('view.reportedIssues.status.unconfirmed', {}) || '',
        },
        {
            id: StatusType.CONFIRMED,
            name: this.$i18n.translate('view.reportedIssues.status.confirmed', {}) || '',
        },
        {
            id: StatusType.ACKNOWLEDGED,
            name: this.$i18n.translate('view.reportedIssues.status.acknowledged', {}) || '',
        },
        {
            id: StatusType.FIXED,
            name: this.$i18n.translate('view.reportedIssues.status.fixed', {}) || '',
        },
        {
            id: StatusType.INVALID,
            name: this.$i18n.translate('view.reportedIssues.status.invalid', {}) || '',
        },
        {
            id: StatusType.BY_DESIGN,
            name: this.$i18n.translate('view.reportedIssues.status.byDesign', {}) || '',
        },
        {
            id: StatusType.EXPIRED,
            name: this.$i18n.translate('view.reportedIssues.status.expired', {}) || '',
        },
        {
            id: StatusType.DUPLICATED,
            name: this.$i18n.translate('view.reportedIssues.status.duplicated', {}) || '',
        },
        {
            id: StatusType.CLOSED,
            name: this.$i18n.translate('view.reportedIssues.status.closed', {}) || '',
        },
    ];

    private platforms: {
        id: string,
        name: string,
    }[] = [];

    // Set the sort by choices for the filter
    private sortByChoices: {
        id: number,
        name: string,
        disabled: boolean,
    }[] = [
        {
            id: this.searchByType.TOP_VOTED,
            name: this.$i18n.translate('view.reportedIssues.sortBy.topVoted', {}) || '',
            disabled: false},
        {
            id: this.searchByType.NEWEST,
            name: this.$i18n.translate('view.reportedIssues.sortBy.newest', {}) || '',
            disabled: false},
        {
            id: this.searchByType.OLDEST,
            name: this.$i18n.translate('view.reportedIssues.sortBy.oldest', {}) || '',
            disabled: false},
    ];

    private timeout: number = 0;

    /**
     * currentGame Watcher
     */
    @Watch('getCurrentGame')
    public async watchCurrentGame(): Promise<void> {
        this.platforms = [];
        if (typeof this.getCurrentGame !== 'undefined' && this.getCurrentGame !== null) {
            for (const platform of this.getCurrentGame.platforms) {
                this.platforms.push({
                    id: platform.prefix,
                    name: platform.name,
                });
            }
        }

        this.filter.category = undefined;
        this.filter.orderByDate = 'desc'; // default order by newest issues
        // this.scrollToElement('.c-issues-list__top');
    }

    @Watch('stateName')
    public async checkStateName(): Promise<void> {
        await this.fetchFilteredIssues();

        // Customer details for authenticated user are fetched during component mount.
        if (typeof this.getCustomerDetails === 'object' && 'votesLeft' in this.getCustomerDetails) {
            this.numberOfVoteLeft = this.getCustomerDetails.votesLeft;
        }
    }

    @Watch('reportStage')
    public async updateGameSelectedInReport(): Promise<void> {
        if (typeof this.reportStage.game !== 'undefined') {
            this.filter.gameId = this.reportStage.game;
        }
        if (this.filter.searchTerm === '') {
            return ;
        }
        await this.fetchFilteredIssues();
    }

    /**
     * Scroll effect.
     */
    public scrollToElement(selector: string): void {
        const element: HTMLElement | null = document.querySelector(selector);
        if (null !== element) {
            window.scroll({
                top: element.offsetTop + 600,
                behavior: 'smooth',
            });
        }
    }

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {
        this.currentRoute = await this.$router.currentRoute.path;
        // Get the games
        try {
            this.state.start();
            await this.fetchGames();
            await this.fetchPlatforms();
            // If there is param gameSlug then Update game and masthead image
            if (undefined !== this.$route.params.gameSlug) {
                const game: GameInterface | undefined = this.getGames.find((item: GameInterface) => {
                    return item.slug === this.$route.params.gameSlug;
                });
                this.updateCurrentGame(game);
                if (game !== undefined && game.branding !== null && game.branding.key_art !== null) {
                    this.updateMastheadImage(game.branding.key_art.url);
                }
            }

            // Set the search term and filter only inside the reported issues and my issues
            if (this.showSearchField) {
                this.searchString = this.getSearchTerm;
                this.filter.searchTerm = this.searchString;
            }

            // Default order
            this.filter.orderByDate = 'desc';

            await this.fetchFilteredIssues();

            // Show all platforms when not game-specific
            if (undefined === this.$route.params.gameSlug) {
                this.platforms = [];
                for (const platform of this.getPlatforms) {
                    this.platforms.push({
                        id: platform.prefix,
                        name: platform.name,
                    });
                }
            }

            this.state.complete();
        } catch (e) {
            this.state.failed(e.message);
        }

        // Gets votes left counter from customer details
        if (this.currentRoute === '/my-issues') {
            await this.fetchCustomerDetails();
        }
    }

    /**
     * Destroyed lifecycle hook
     *
     */
    public async destroyed(): Promise<void> {
        this.updateCurrentGame(undefined);
        this.removeCustomerDetails();
    }

    /**
     * Pagination - Navigation to pages
     */
    public async paginate(action: string): Promise<void> {
        await this.fetchIssues({
            filters: this.filter,
            stateName: this.stateName,
            path: this.pagination[action],
            showIssueSummary: this.showIssueSummary,
        });
        switch (action) {
            case 'first': {
                this.currentPage = 1;
                break;
            }
            case 'previous': {
                this.currentPage--;
                break;
            }
            case 'next': {
                this.currentPage++;
                break;
            }
            case 'last': {
                this.currentPage = this.totalPages;
                break;
            }
        }
    }

    /**
     * Fetch Filtered Issues
     */
    public async fetchFilteredIssues(): Promise<void> {
        if (typeof this.getCurrentGame !== 'undefined') {
            this.versionList = this.getCurrentGame.versions;
        }

        if (this.selected.game !== null) {
            for (const eachGame of this.getGames) {
                if (eachGame.id === +this.selected.game) {
                    this.versionList = eachGame.versions;
                }
            }
        }

        if (this.filter.gameId === undefined) {
            this.filter.gameId = undefined !== this.getCurrentGame ? this.getCurrentGame.id : undefined;
        }

        if (typeof this.updateCurrentGameVersion !== 'undefined' && this.updateCurrentGameVersion !== null
            && this.versionKey === '') {
            this.filter.version = this.updateCurrentGameVersion;
            this.versionKey = this.updateCurrentGameVersion;
        }
        // if no game is selected, remove the filter on version as well
        if (typeof this.filter.gameId === 'undefined') {
            this.filter.version = null;
        }
        await this.fetchIssues({
            filters: this.filter,
            stateName: this.stateName, // when = my -> load only my created issues
            showIssueSummary: this.showIssueSummary,
        });

        if (this.totalPages === 0) {
            this.currentPage = 0;
        }
        if (this.totalPages !== 0) {
            this.currentPage = 1;
        }
    }

    /**
     * Keyword changed event.
     * @param {string} keyword
     */
    public async keywordChanged(keyword?: string): Promise<void> {
        try {
            this.updateSearchTerm(this.searchString);
            this.filter.searchTerm = this.searchString;
            this.state.start();
            await this.fetchFilteredIssues();
            this.state.complete();
        } catch (e) {
            // do nothing
        }
    }

    /**
     * Filter By Type
     */
    public async filterBy(type: string, id: any): Promise<void> {
        // Set currentPage number to default
        this.currentPage = 1;

        if (type === 'sortBy') {
            this.selected.sort = id;
            switch (id) {
                case this.searchByType.TOP_VOTED: {
                    // todo filter by top voted
                    this.filter.orderByVote = this.filter.orderByVote === 'desc' ? '' : 'desc';
                    // clear orderByDate
                    this.filter.orderByDate = undefined;
                    break;
                }
                case this.searchByType.NEWEST: {
                    this.filter.orderByDate = 'desc';
                    // clear orderByVote
                    this.filter.orderByVote = undefined;
                    break;
                }
                case this.searchByType.OLDEST: {
                    this.filter.orderByDate = 'asc';
                    // clear orderByVote
                    this.filter.orderByVote = undefined;
                    break;
                }
            }
        }

        if (type === 'status') {
            this.selectedStatus = id;
            this.selected.status = this.selected.status === id ? null : id;
            switch (id) {
                case this.statusType.UNCONFIRMED: {
                    this.filter.status = this.filter.status === 'unconfirmed' ? '' : ['unconfirmed', 'expiry_notice'];
                    break;
                }
                case this.statusType.CONFIRMED: {
                    this.filter.status = this.filter.status === 'confirmed' ? '' : 'confirmed';
                    break;
                }
                case this.statusType.ACKNOWLEDGED: {
                    this.filter.status = this.filter.status === 'acknowledged' ? '' : 'acknowledged';
                    break;
                }
                case this.statusType.FIXED: {
                    this.filter.status = this.filter.status === 'fixed' ? '' : 'fixed';
                    break;
                }
                case this.statusType.BY_DESIGN: {
                    this.filter.status = this.filter.status === 'by_design' ? '' : 'by_design';
                    break;
                }
                case this.statusType.INVALID: {
                    this.filter.status = this.filter.status === 'invalid' ? '' : 'invalid';
                    break;
                }
                case this.statusType.EXPIRED: {
                    this.filter.status = this.filter.status === 'expired' ? '' : 'expired';
                    break;
                }
                case this.statusType.DUPLICATED: {
                    this.filter.status = this.filter.status === 'duplicated' ? '' : 'duplicated';
                    break;
                }
                case this.statusType.CLOSED: {
                    this.filter.status = this.filter.status === 'closed' ? '' : 'closed';
                    break;
                }
            }
        }

        if (type === 'category') {
            this.selected.category = this.selected.category === id ? null : id;
            const stringId: undefined | string = (null === id) ? undefined : id.toString();
            this.filter.category = this.filter.category === stringId ? undefined : stringId;
        }

        if (type === 'platform') {
            this.selected.platform = this.selected.platform === id ? null : id;
            this.filter.platform = (this.filter.platform == null || this.filter.platform !== id) ? id : null;
        }

        if (type === 'version') {
            this.selected.version = this.selected.version === id ? null : id;
            this.filter.version = (this.filter.version == null || this.filter.version !== id) ? id : null;
        }

        if (type === 'game') {
            this.selected.game = this.selected.game === id ? null : id;
            this.filter.gameId = (this.filter.gameId == null || this.filter.gameId !== id) ? id : null;
        }

        this.state.start();
        await this.fetchFilteredIssues();
        this.state.complete();
    }

}
